import React, { useContext, useEffect, useRef, useState } from 'react'
import { RootContext as context } from '~/state'

interface FlowInputNodeProps {
  icon: React.ReactNode
  label: string
  description?: string
}

export const FlowInputNode = ({ icon, label, description }: FlowInputNodeProps): JSX.Element => {
  const { graph, register, highlight } = useContext(context)

  const [isFocused, setIsFocused] = useState(false)

  const nodeRef = useRef<HTMLDivElement>(null)

  const key = `input-${label}`

  useEffect(() => {
    const isRegistered = graph?.nodes?.find((node) => node.key === key)

    if (!isRegistered) {
      register(key, nodeRef)
    }
  }, [key, graph, register])

  const isFaded = (graph?.highlight?.length ?? 0 > 0) && !graph?.highlight?.includes(key)

  return (
    <div
      className="input-container"
      style={{ opacity: isFaded ? 0.2 : 1, transition: 'opacity', transitionDuration: '300ms' }}
      onPointerEnter={() => {
        setIsFocused(true)
        highlight(key)
      }}
      onPointerLeave={() => {
        setIsFocused(false)
        highlight(undefined)
      }}
    >
      <div className="input-node" ref={nodeRef}>
        {icon}
      </div>
      <div className="input-label">
        <h2>
          <span title={description}>{label}</span>
        </h2>
      </div>
      {isFocused ? (
        <div className="input-description">
          <p>{description}</p>
        </div>
      ) : null}
      <style global jsx>{`
        .input-node > svg {
          transform: rotate(-45deg) scale(1.5);
        }
      `}</style>
      <style jsx>{`
        .input-container {
          display: flex;
          align-items: center;
          box-sizing: content-box;
        }

        .input-node {
          min-width: 34px;
          min-height: 34px;
          box-sizing: content-box;
          margin-top: 12px;
          margin-bottom: 12px;
          border-radius: 34px;
          border-top-right-radius: 0;
          border: 2px solid #333;
          background: #fcfcfc;
          display: flex;
          justify-content: center;
          align-items: center;
          transform: rotate(45deg) translate(-14px, 14px);
        }

        .input-label {
          min-width: 50px;
          width: 50px;
          font-family: 'Inter', sans-serif;
          font-weight: 300;
          text-align: 'right';
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          transform: translate(-115px, -2px);
        }

        .input-label > h2 {
          font-size: 12px;
          white-space: nowrap;
          user-select: none;
        }

        .input-description {
          min-width: 150px;
          width: 150px;
          height: 15px;
          overflow: visible;
          font-family: 'Inter', sans-serif;
          font-weight: 200;
          text-align: right;
          flex-direction: column;
          align-items: flex-end;
          transform: translate(-265px, 5px);
        }

        .input-description > p {
          font-size: 11px;
          line-height: 15px;
          font-weight: 200;
          color: #333;
          margin: 0;
          margin-top: 12px;
          user-select: none;
          z-index: 2;
        }
      `}</style>
    </div>
  )
}
